import React, { useState, useEffect } from 'react';
import { Form } from 'semantic-ui-react';
import { connect } from 'react-redux';

import { ModalHeader } from '@modules/common/components/BaseModal/ModalHeader';
import { ModalContent } from '@modules/common/components/BaseModal/ModalContent';
import { ModalActions } from '@modules/common/components/BaseModal/ModalActions';
import SecondaryButton from '@modules/common/components/buttons/SecondaryButton';
import NewsletterResource from '@src/services/Identity/NewsletterResource';
import { alertError } from '@src/lib/alert';
import SentryUtils from '@src/lib/sentry';
import { NewsletterContact } from '@src/meta/types/newsletter';
import {
  SentinelUser,
  Claims,
} from '@src/config/default/modules/Authentication/components/types/user';
import { StoreState } from '@src/meta/types/store';
import config from '@config/config';
import { ApiError } from '@src/lib/api';
import BaseModal from '@modules/common/components/BaseModal';
import Header from '@modules/common/components/Header';
import Checkbox from '@modules/common/components/Checkbox';

const newsletterCheckboxText =
  'I would like to receive latest news and information on Sentinel Hub.';
const NEWSLETTER_SUBSCRIPTION_LIST_ID = 5;

export type PropsFromParent = {
  setFieldValue: (field: string, value: any) => void;
  newsletter_consent: boolean;
  readOnly?: boolean;
  openNewsletterModal: boolean;
  setOpenNewsletterModal: (b: boolean) => void;
};

type PropsFromStore = {
  configurableUser: SentinelUser | undefined;
};

const NewsletterWidget = ({
  configurableUser,
  setFieldValue,
  newsletter_consent,
  readOnly,
  openNewsletterModal,
  setOpenNewsletterModal,
}: PropsFromStore & PropsFromParent) => {
  const [fetchingSubscription, setFetchingSubscriptions] = useState<boolean>(false);

  if (!configurableUser) {
    return null;
  }

  useEffect(() => {
    const fetchAndSetSubscription = async () => {
      try {
        setFetchingSubscriptions(true);
        const res = await NewsletterResource.getContact({ email: configurableUser.email });
        setFieldValue('contact', res);
        if (isSubscribed(res)) {
          setFieldValue('newsletter_consent', true);
          setFieldValue('initialNewsletterConsent', true);
        }
      } catch (err) {
        if ((err as ApiError<any>).status !== 404) {
          const errorMessage = 'Something went wrong while fetching newsletter subscription';
          alertError(errorMessage);
          SentryUtils.captureAPIError(err as any, errorMessage);
        }
      }
      setFetchingSubscriptions(false);
    };
    if (configurableUser.email) {
      fetchAndSetSubscription();
    }
  }, []);

  const isSubscribed = (contact?: NewsletterContact) => {
    return contact
      ? Boolean(
          contact.listIds &&
            contact.listIds.length > 0 &&
            contact.listIds.includes(NEWSLETTER_SUBSCRIPTION_LIST_ID),
        )
      : false;
  };

  return (
    <MarketingPermissionsFormFields
      fetchingSubscription={fetchingSubscription}
      newsletter_consent={newsletter_consent}
      setFieldValue={setFieldValue}
      handleOpenNewsletterModal={(open: boolean) => setOpenNewsletterModal(open)}
      openNewsletterModal={openNewsletterModal}
      readOnly={readOnly}
    />
  );
};

const mapStateToProps = (state: StoreState<SentinelUser, Claims>): PropsFromStore => ({
  configurableUser: state.auth.configurableUser,
});

export default connect(mapStateToProps)(NewsletterWidget);

export function createNewsletterContact(payload: NewsletterContact) {
  return NewsletterResource.createContact(payload);
}

export function deleteNewsletterContact(email: string) {
  return NewsletterResource.deleteContact({ email });
}

const MarketingPermissionsFormFields = ({
  newsletter_consent,
  setFieldValue,
  fetchingSubscription,
  openNewsletterModal,
  handleOpenNewsletterModal,
  readOnly,
}: {
  setFieldValue: (s: 'newsletter_consent', value: boolean) => void;
  fetchingSubscription: boolean;
  newsletter_consent: boolean;
  openNewsletterModal: boolean;
  handleOpenNewsletterModal: (open: boolean) => void;
  readOnly?: boolean;
}) => {
  if (!config.showMarketingPermissions) {
    return null;
  }
  return (
    <>
      <NewsletterInfoModal
        openNewsletterModal={openNewsletterModal}
        handleOpenNewsletterModal={handleOpenNewsletterModal}
      />
      <Header content="Marketing permissions" />

      <Form.Field>
        <Checkbox
          name="newsletter_consent"
          onChange={() => setFieldValue('newsletter_consent', !newsletter_consent)}
          label={newsletterCheckboxText}
          checked={newsletter_consent}
          disabled={fetchingSubscription}
          readOnly={readOnly}
        />
      </Form.Field>

      <Form.Field>
        <p style={{ fontSize: '0.8em' }}>
          We send a newsletter every two months. You can always unsubscribe anytime directly from
          within an e-mail.
        </p>
      </Form.Field>
    </>
  );
};

const NewsletterInfoModal = ({
  openNewsletterModal,
  handleOpenNewsletterModal,
}: {
  openNewsletterModal: boolean;
  handleOpenNewsletterModal: (open: boolean) => void;
}) => {
  return (
    <>
      <BaseModal open={openNewsletterModal}>
        {() => (
          <>
            <ModalHeader>Confirm the subscription on your account mail.</ModalHeader>
            <ModalContent>
              <p>
                To finalize the subscription to the Sentinel Hub newsletter you should accept the
                confirmation mail that was just sent to you.
              </p>
              <p>
                <i>
                  <b>Note that the mail can take several minutes to arrive.</b>
                </i>
              </p>
            </ModalContent>
            <ModalActions>
              <SecondaryButton
                icon="checkmark"
                content="Acknowledge"
                onClick={() => handleOpenNewsletterModal(false)}
              />
            </ModalActions>
          </>
        )}
      </BaseModal>
    </>
  );
};
